import { useEffect, useState, useCallback } from "react";
import { Plus, Search, Eye, Edit2, X } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { api, formatApiErrorDetail } from "../lib/api";

const EMPTY = { email: "", password: "", name: "", qualification: "", experience_years: 0, category: "",
  consultation_fee: 0, video_fee: 0, opd_fee: 0, image_url: "", about: "" };

function FormFields({ form, setForm, editing, categories }) {
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const updNum = (k) => (e) => setForm((f) => ({ ...f, [k]: Number(e.target.value) || 0 }));
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div className="space-y-1.5"><label className="text-xs font-medium">Email</label>
        <Input data-testid="doctor-form-email" value={form.email} onChange={upd("email")} disabled={editing} /></div>
      {!editing && (
        <div className="space-y-1.5"><label className="text-xs font-medium">Password</label>
          <div className="flex gap-2">
            <Input data-testid="doctor-form-password" value={form.password} onChange={upd("password")} />
            <Button type="button" variant="outline" size="sm" onClick={() => setForm((f) => ({ ...f, password: Math.random().toString(36).slice(-10) + "A1!" }))}>Gen</Button>
          </div></div>
      )}
      <div className="space-y-1.5"><label className="text-xs font-medium">Name</label>
        <Input data-testid="doctor-form-name" value={form.name} onChange={upd("name")} /></div>
      <div className="space-y-1.5"><label className="text-xs font-medium">Qualification</label>
        <Input data-testid="doctor-form-qualification" value={form.qualification} onChange={upd("qualification")} /></div>
      <div className="space-y-1.5"><label className="text-xs font-medium">Experience (years)</label>
        <Input type="number" data-testid="doctor-form-experience" value={form.experience_years} onChange={updNum("experience_years")} /></div>
      <div className="space-y-1.5"><label className="text-xs font-medium">Category</label>
        <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
          <SelectTrigger data-testid="doctor-form-category"><SelectValue placeholder="Select" /></SelectTrigger>
          <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
        </Select></div>
      <div className="space-y-1.5"><label className="text-xs font-medium">Consultation fee ₹</label>
        <Input type="number" data-testid="doctor-form-cons" value={form.consultation_fee} onChange={updNum("consultation_fee")} /></div>
      <div className="space-y-1.5"><label className="text-xs font-medium">Video fee ₹</label>
        <Input type="number" data-testid="doctor-form-video" value={form.video_fee} onChange={updNum("video_fee")} /></div>
      <div className="space-y-1.5"><label className="text-xs font-medium">OPD fee ₹</label>
        <Input type="number" data-testid="doctor-form-opd" value={form.opd_fee} onChange={updNum("opd_fee")} /></div>
      <div className="space-y-1.5 sm:col-span-2"><label className="text-xs font-medium">Image URL</label>
        <Input data-testid="doctor-form-image" value={form.image_url || ""} onChange={upd("image_url")} placeholder="https://..." />
        {form.image_url && <img src={form.image_url} alt="" className="mt-1 h-16 w-16 rounded-xl object-cover" />}</div>
    </div>
  );
}

export default function AdminDoctors() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [activeOnly, setActiveOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [viewId, setViewId] = useState(null);
  const [viewData, setViewData] = useState(null);
  const [clinics, setClinics] = useState([]);
  const [newAssocClinic, setNewAssocClinic] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), page_size: "20" });
      if (q) params.set("q", q);
      if (category) params.set("category", category);
      if (activeOnly) params.set("active", "true");
      const { data } = await api.get(`/admin/doctors?${params.toString()}`);
      setItems(data.items); setTotal(data.total);
    } finally { setLoading(false); }
  }, [q, category, activeOnly, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
    api.get("/admin/clinics?page_size=200").then((r) => setClinics(r.data.items));
  }, []);

  useEffect(() => {
    if (!viewId) return;
    api.get(`/admin/doctors/${viewId}`).then((r) => setViewData(r.data));
  }, [viewId]);

  const openCreate = () => { setEditing(null); setForm({ ...EMPTY }); setOpenForm(true); };
  const openEdit = (d) => {
    setEditing(d.id);
    setForm({
      email: d.email, password: "", name: d.name, qualification: d.qualification,
      experience_years: d.experience_years, category: d.category,
      consultation_fee: d.consultation_fee, video_fee: d.video_fee, opd_fee: d.opd_fee,
      image_url: d.image_url || "", about: d.about || "",
    });
    setOpenForm(true);
  };
  const submitForm = async () => {
    try {
      if (editing) {
        const payload = { ...form }; delete payload.email; delete payload.password;
        await api.patch(`/admin/doctors/${editing}`, payload);
        toast.success("Doctor updated");
      } else {
        await api.post("/admin/doctors", form);
        toast.success("Doctor added");
      }
      setOpenForm(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const toggleActive = async (d) => {
    try {
      await api.patch(`/admin/doctors/${d.id}`, { is_active: !d.is_active });
      toast.success(`Doctor ${!d.is_active ? "activated" : "deactivated"}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const addAssoc = async () => {
    if (!newAssocClinic || !viewId) return;
    try {
      await api.post("/admin/associations", { doctor_id: viewId, clinic_id: newAssocClinic });
      toast.success("Association added");
      const { data } = await api.get(`/admin/doctors/${viewId}`); setViewData(data);
      setNewAssocClinic("");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const removeAssoc = async (aid) => {
    try {
      await api.delete(`/admin/associations/${aid}`);
      const { data } = await api.get(`/admin/doctors/${viewId}`); setViewData(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <AdminLayout title="Doctors" subtitle={`${total} total`} action={
      <Button data-testid="admin-add-doctor-btn" onClick={openCreate} className="rounded-full"><Plus className="h-4 w-4 mr-1" />Add Doctor</Button>
    }>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="admin-doctors-search" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search name/email/category" className="pl-9 rounded-full" />
        </div>
        <Select value={category || "all"} onValueChange={(v) => { setCategory(v === "all" ? "" : v); setPage(1); }}>
          <SelectTrigger data-testid="admin-doctors-cat-filter" className="w-44 rounded-full"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-sm">
          <Switch data-testid="admin-doctors-active-only" checked={activeOnly} onCheckedChange={(v) => { setActiveOnly(v); setPage(1); }} />Active only
        </label>
      </div>

      <div data-testid="admin-doctors-table" className="bg-white border border-border rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="bg-secondary/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="text-left p-3">Doctor</th><th className="text-left p-3">Category</th>
                <th className="text-right p-3">Exp</th><th className="text-right p-3">Fees</th>
                <th className="text-right p-3">Rating</th><th className="text-center p-3">Active</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">Loading…</td></tr>
                : items.length === 0 ? <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">No doctors</td></tr>
                : items.map((d) => (
                <tr key={d.id} data-testid={`admin-doctor-row-${d.id}`} className="border-t border-border hover:bg-secondary/30">
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      {d.image_url && <img src={d.image_url} alt="" className="h-8 w-8 rounded-full object-cover" />}
                      <div><div className="font-medium">{d.name}</div><div className="text-xs text-muted-foreground">{d.email}</div></div>
                    </div></td>
                  <td className="p-3">{d.category}</td>
                  <td className="p-3 text-right">{d.experience_years}y</td>
                  <td className="p-3 text-right text-xs tabular-nums">₹{d.consultation_fee}/{d.video_fee}/{d.opd_fee}</td>
                  <td className="p-3 text-right tabular-nums">{d.rating?.toFixed(1)} ({d.total_reviews})</td>
                  <td className="p-3 text-center">
                    <Switch data-testid={`admin-doctor-toggle-${d.id}`} checked={d.is_active} onCheckedChange={() => toggleActive(d)} />
                  </td>
                  <td className="p-3 text-right">
                    <Button size="sm" variant="ghost" data-testid={`admin-doctor-view-${d.id}`} onClick={() => setViewId(d.id)}><Eye className="h-4 w-4" /></Button>
                    <Button size="sm" variant="ghost" data-testid={`admin-doctor-edit-${d.id}`} onClick={() => openEdit(d)}><Edit2 className="h-4 w-4" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {total > 20 && (
        <div className="mt-4 flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Page {page} of {Math.ceil(total / 20)}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
            <Button size="sm" variant="outline" disabled={page >= Math.ceil(total / 20)} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        </div>
      )}

      <Dialog open={openForm} onOpenChange={setOpenForm}>
        <DialogContent data-testid="admin-doctor-form-modal" className="sm:max-w-2xl rounded-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit Doctor" : "Add Doctor"}</DialogTitle></DialogHeader>
          <FormFields form={form} setForm={setForm} editing={!!editing} categories={categories} />
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOpenForm(false)} className="rounded-full">Cancel</Button>
            <Button data-testid="admin-doctor-save-btn" onClick={submitForm} className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground hover:text-primary-foreground">{editing ? "Save" : "Add"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Sheet open={!!viewId} onOpenChange={(o) => { if (!o) { setViewId(null); setViewData(null); } }}>
        <SheetContent data-testid="admin-doctor-view-drawer" className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader><SheetTitle>{viewData?.doctor?.name || "Doctor"}</SheetTitle></SheetHeader>
          {viewData && (
            <div className="space-y-4 mt-4">
              <div className="bg-secondary/40 rounded-xl p-3 text-sm">
                <div className="font-medium">{viewData.doctor.email}</div>
                <div className="text-muted-foreground text-xs">{viewData.doctor.category} · {viewData.doctor.experience_years}y · {viewData.doctor.qualification}</div>
                <div className="text-xs mt-1">Rating: <span className="font-medium">{viewData.doctor.rating?.toFixed(1)}</span> ({viewData.doctor.total_reviews} reviews)</div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center text-xs">
                <div className="bg-white border border-border rounded-xl p-2"><div className="text-muted-foreground">Total appts</div><div className="font-semibold text-base">{viewData.appointment_count}</div></div>
                <div className="bg-white border border-border rounded-xl p-2"><div className="text-muted-foreground">Completed</div><div className="font-semibold text-base">{viewData.completed_count}</div></div>
                <div className="bg-white border border-border rounded-xl p-2"><div className="text-muted-foreground">Earnings</div><div className="font-semibold text-base">₹{viewData.earnings_total.toLocaleString("en-IN")}</div></div>
              </div>
              <div>
                <div className="text-sm font-semibold mb-2">Clinic associations ({viewData.associations.length})</div>
                <div className="space-y-2">
                  {viewData.associations.map((a) => (
                    <div key={a.id} data-testid={`assoc-row-${a.id}`} className="flex items-center justify-between bg-white border border-border rounded-xl p-2 text-sm">
                      <div><div className="font-medium">{a.clinic?.name}</div><div className="text-xs text-muted-foreground">{a.clinic?.city}</div></div>
                      <Button size="sm" variant="ghost" data-testid={`assoc-remove-${a.id}`} onClick={() => removeAssoc(a.id)}><X className="h-4 w-4" /></Button>
                    </div>
                  ))}
                  {viewData.associations.length === 0 && <div className="text-xs text-muted-foreground">No clinics yet.</div>}
                </div>
                <div className="mt-3 flex gap-2">
                  <Select value={newAssocClinic} onValueChange={setNewAssocClinic}>
                    <SelectTrigger data-testid="assoc-add-clinic-select" className="flex-1"><SelectValue placeholder="Add clinic…" /></SelectTrigger>
                    <SelectContent>
                      {clinics.filter((c) => !viewData.associations.some((a) => a.clinic_id === c.id)).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button data-testid="assoc-add-btn" onClick={addAssoc} disabled={!newAssocClinic}>Add</Button>
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </AdminLayout>
  );
}
